import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  MenuItem,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { EmptyState, EntityRow, PageHeader } from '../components/ui';
import DesignSystem from '../designSystem';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import {
  advancedSearch,
  ApiBusiness,
  fetchCategories,
  getBusinessLogoUrl,
} from '../services/api';
import { useT } from '../i18n/useT';

function fieldSx() {
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: DesignSystem.borderRadius.lg,
      backgroundColor: DesignSystem.colors.surface,
      '& fieldset': { border: 'none' },
      '&:hover fieldset': { border: 'none' },
      '&.Mui-focused fieldset': { border: 'none' },
    },
    '& input, & .MuiSelect-select': { color: DesignSystem.colors.textPrimary },
    '& label': { color: DesignSystem.colors.textSecondary },
    '& label.Mui-focused': { color: DesignSystem.colors.primary },
  };
}

export default function AdvancedSearchPage() {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [radius, setRadius] = useState('');
  const [sortBy, setSortBy] = useState<'distance' | 'name' | 'recent'>('distance');
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [results, setResults] = useState<ApiBusiness[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.token) return;
    fetchCategories(getApiLanguage(), user.token)
      .then((res) => {
        setCategories((res.categories || []).map((c) => ({ id: c.id, name: c.name })));
      })
      .catch(() => {});
  }, [user?.token]);

  const runSearch = async () => {
    if (!user?.token) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    setResults([]);
    try {
      const latN = lat.trim() ? parseFloat(lat) : undefined;
      const lngN = lng.trim() ? parseFloat(lng) : undefined;
      const radN = radius.trim() ? parseFloat(radius) : undefined;
      const catIds = categoryId ? [parseInt(categoryId, 10)] : undefined;
      const res = await advancedSearch(
        user.token,
        {
          query: query.trim() || undefined,
          categoryIds: catIds,
          lat: latN,
          lng: lngN,
          radius: radN,
          sortBy,
        },
        getApiLanguage()
      );
      setResults(res.businesses || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('advancedSearchFailed'));
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const openBusiness = (b: ApiBusiness) => {
    const username = b.username?.replace('@', '') || b.id.toString();
    navigate(`/${username}`);
  };

  return (
    <AppShell title={t('advancedSearchTitle')} showBackButton maxWidth="feed" showCompanion hideTitle>
      <PageHeader title={t('advancedSearchTitle')} subtitle={t('advancedSearchSubtitle')} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
        <TextField label={t('advancedSearchKeywords')} value={query} onChange={(e) => setQuery(e.target.value)} fullWidth sx={fieldSx()} />
        <TextField
          select
          label={t('advancedSearchCategoryOptional')}
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          fullWidth
          sx={fieldSx()}
        >
          <MenuItem value="">{t('advancedSearchAny')}</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField label={t('advancedSearchLatitude')} value={lat} onChange={(e) => setLat(e.target.value)} sx={{ flex: 1, minWidth: 120, ...fieldSx() }} />
          <TextField label={t('advancedSearchLongitude')} value={lng} onChange={(e) => setLng(e.target.value)} sx={{ flex: 1, minWidth: 120, ...fieldSx() }} />
          <TextField label={t('advancedSearchRadiusKm')} value={radius} onChange={(e) => setRadius(e.target.value)} sx={{ flex: 1, minWidth: 120, ...fieldSx() }} />
        </Box>
        <TextField
          select
          label={t('advancedSearchSort')}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'distance' | 'name' | 'recent')}
          fullWidth
          sx={fieldSx()}
        >
          <MenuItem value="distance">{t('advancedSearchSortDistance')}</MenuItem>
          <MenuItem value="name">{t('advancedSearchSortName')}</MenuItem>
          <MenuItem value="recent">{t('advancedSearchSortRecent')}</MenuItem>
        </TextField>
        <Button
          variant="contained"
          onClick={runSearch}
          disabled={loading}
          aria-label={t('advancedSearchRunAria')}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            alignSelf: 'flex-start',
            borderRadius: DesignSystem.borderRadius.pill,
            px: 2.5,
            boxShadow: 'none',
            bgcolor: DesignSystem.colors.action,
            '&:hover': { bgcolor: DesignSystem.colors.actionDark, boxShadow: 'none' },
          }}
        >
          {loading ? t('searching') : t('advancedSearchRun')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : searched && results.length === 0 ? (
        <EmptyState title={t('advancedSearchEmptyTitle')} body={t('advancedSearchEmptyBody')} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {results.map((b) => (
            <EntityRow
              key={b.id}
              title={b.name}
              meta={b.subcategory?.name || b.category?.name}
              avatarSrc={b.logo ? getBusinessLogoUrl(b.logo) || undefined : undefined}
              onClick={() => openBusiness(b)}
            />
          ))}
        </Box>
      )}
    </AppShell>
  );
}
